import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Booth } from '@festpix/types';

export interface BoothWithBalance extends Booth {
  walletBalance?: number;
}

export interface CreateBoothParams {
  name: string;
}

export interface UpdateBoothParams {
  boothId: string;
  name?: string;
  vendorIds?: string[];
}

export interface Vendor {
  id: string;
  name: string;
  email: string;
}

/**
 * Create a new booth with an automatically generated wallet
 */
export async function createBooth(params: CreateBoothParams): Promise<BoothWithBalance> {
  const { name } = params;

  // Create a wallet for the booth
  const walletRef = await addDoc(collection(db, 'wallets'), {
    balance: 0,
    ownerId: 'booth',
    qrCode: '',
    associatedWallets: [],
    type: 'booth',
    boothName: name,
  });

  // Create the booth document
  const boothRef = await addDoc(collection(db, 'booths'), {
    name,
    mainWalletId: walletRef.id,
    vendorIds: [],
    createdAt: new Date(),
  });

  return {
    id: boothRef.id,
    name,
    mainWalletId: walletRef.id,
    vendorIds: [],
    walletBalance: 0,
  };
}

/**
 * Get all booths with their wallet balances
 * Optimized: Fetches all wallets once and maps them to booths (avoids N+1 queries)
 */
export async function getAllBooths(): Promise<BoothWithBalance[]> {
  // Fetch booths and wallets in parallel
  const [boothsSnapshot, walletsSnapshot] = await Promise.all([
    getDocs(collection(db, 'booths')),
    getDocs(collection(db, 'wallets')),
  ]);

  // Build wallet balance lookup map
  const walletBalances = new Map<string, number>();
  walletsSnapshot.docs.forEach((walletDoc) => {
    walletBalances.set(walletDoc.id, walletDoc.data().balance || 0);
  });

  // Map booths with wallet balances
  const booths: BoothWithBalance[] = boothsSnapshot.docs.map((boothDoc) => {
    const boothData = boothDoc.data();
    return {
      id: boothDoc.id,
      name: boothData.name,
      mainWalletId: boothData.mainWalletId,
      vendorIds: boothData.vendorIds || [],
      walletBalance: boothData.mainWalletId ? walletBalances.get(boothData.mainWalletId) || 0 : 0,
    };
  });

  return booths;
}

/**
 * Get all users with vendor role
 */
export async function getVendors(): Promise<Vendor[]> {
  const usersSnapshot = await getDocs(
    query(collection(db, 'users'), where('role', '==', 'vendor'))
  );

  return usersSnapshot.docs.map((doc) => ({
    id: doc.id,
    name: doc.data().name || 'Sem nome',
    email: doc.data().email || '',
  }));
}

/**
 * Update booth details
 */
export async function updateBooth(params: UpdateBoothParams): Promise<void> {
  const { boothId, name, vendorIds } = params;
  const boothRef = doc(db, 'booths', boothId);

  const updates: Record<string, unknown> = {};
  if (name !== undefined) updates.name = name;
  if (vendorIds !== undefined) updates.vendorIds = vendorIds;

  await updateDoc(boothRef, updates);
}

/**
 * Delete a booth (does not delete the associated wallet)
 */
export async function deleteBooth(boothId: string): Promise<void> {
  await deleteDoc(doc(db, 'booths', boothId));
}

/**
 * Add a vendor to a booth
 * Optimized: Uses direct document reference instead of fetching all booths
 */
export async function addVendorToBooth(boothId: string, vendorId: string): Promise<void> {
  const boothRef = doc(db, 'booths', boothId);
  const boothDoc = await getDoc(boothRef);

  if (!boothDoc.exists()) {
    throw new Error('Estande não encontrado');
  }

  const currentVendorIds = boothDoc.data().vendorIds || [];
  if (currentVendorIds.includes(vendorId)) {
    throw new Error('Vendedor já está associado a este estande');
  }

  // Use arrayUnion for atomic operation
  await updateDoc(boothRef, {
    vendorIds: arrayUnion(vendorId),
  });
}

/**
 * Remove a vendor from a booth
 * Optimized: Uses direct document reference and arrayRemove for atomic operation
 */
export async function removeVendorFromBooth(boothId: string, vendorId: string): Promise<void> {
  const boothRef = doc(db, 'booths', boothId);
  const boothDoc = await getDoc(boothRef);

  if (!boothDoc.exists()) {
    throw new Error('Estande não encontrado');
  }

  // Use arrayRemove for atomic operation
  await updateDoc(boothRef, {
    vendorIds: arrayRemove(vendorId),
  });
}

export type { Booth };
